import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadUsers, removeUser, clearError } from '../features/users/usersSlice';
import { useAuth } from '../hooks/useAuth';
import UserForm from './UserForm';

export default function UsersList() {
  const dispatch = useDispatch();
  const { items, status, error } = useSelector(s => s.users);
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(loadUsers());
  }, [dispatch]);

  const handleDelete = async (id) => {
    if (deleteConfirm === id) {
      await dispatch(removeUser(id));
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const handleClearError = () => {
    dispatch(clearError());
  };

  // Filter users based on search term (name or email)
  const filteredItems = items.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  });

  const primaryColor = '#05aa4be6';
  const pageSize = 20;

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: primaryColor, fontWeight: '600', marginBottom: 0 }}>Users Management</h2>
        <button
          className="btn btn-lg text-white shadow-sm d-flex align-items-center gap-2"
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            backgroundColor: primaryColor,
            border: 'none',
            borderRadius: '0.5rem'
          }}
        >
          {showCreateForm ? (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0a1 1 0 0 1 1 1v6h6a1 1 0 1 1 0 2H9v6a1 1 0 1 1-2 0V9H1a1 1 0 0 1 0-2h6V1a1 1 0 0 1 1-1z"/>
              </svg>
              Create New User
            </>
          )}
        </button>
      </div>

      {currentUser && (
        <div className="alert alert-info d-flex align-items-center" style={{ borderRadius: '0.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
          </svg>
          <div>Logged in as: <strong>{currentUser.email}</strong> ({currentUser.role || 'user'})</div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible d-flex align-items-center" role="alert" style={{ borderRadius: '0.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
          </svg>
          <div>{error}</div>
          <button
            type="button"
            className="btn-close"
            onClick={handleClearError}
          ></button>
        </div>
      )}

      {/* Search Bar */}
      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '0.5rem' }}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ borderRadius: '0.5rem' }}
              />
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => setSearchTerm('')}
                disabled={!searchTerm}
                style={{ borderRadius: '0.5rem' }}
              >
                Clear Search
              </button>
            </div>
          </div>
          {searchTerm && (
            <div className="mt-2">
              <small className="text-muted">
                Showing <strong>{filteredItems.length}</strong> of <strong>{items.length}</strong> users
              </small>
            </div>
          )}
        </div>
      </div>

      {showCreateForm && (
        <div className="row justify-content-center mb-4">
          <div className="col-lg-8 col-xl-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem' }}>
              <div className="card-body">
                <h5 className="card-title" style={{ color: primaryColor }}>Create New User</h5>
                <UserForm
                  onCreated={() => {
                    dispatch(loadUsers());
                    setShowCreateForm(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {status === 'loading' && (
        <div className="text-center my-5">
          <div className="spinner-border" style={{ color: primaryColor }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {status === 'succeeded' && filteredItems.length === 0 && (
        <div className="alert alert-warning d-flex align-items-center" style={{ borderRadius: '0.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
          </svg>
          <div>
            {searchTerm ? 'No users match your search criteria.' : 'No users found. Create your first user above.'}
          </div>
        </div>
      )}

      {filteredItems.length > 0 && (
        <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem' }}>
          <div className="card-body">
            <table className="table table-hover mb-0">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>Name</th>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>Email</th>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>Status</th>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>2FA</th>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>Created</th>
                  <th style={{ color: '#6c757d', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(u => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: u.status === 'active' ? primaryColor : '#6c757d',
                          borderRadius: '0.5rem',
                          padding: '0.35rem 0.75rem'
                        }}
                      >
                        {u.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {u.twoFactorEnabled ? (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: u.twoFactorVerified ? primaryColor : '#ffc107',
                            borderRadius: '0.5rem',
                            padding: '0.35rem 0.75rem'
                          }}
                        >
                          {u.twoFactorVerified ? 'Verified' : 'Pending'}
                        </span>
                      ) : (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#6c757d',
                            borderRadius: '0.5rem',
                            padding: '0.35rem 0.75rem'
                          }}
                        >
                          Disabled
                        </span>
                      )}
                    </td>
                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm text-white"
                          onClick={() => setEditing(u)}
                          style={{ backgroundColor: primaryColor, border: 'none', borderRadius: '0.5rem' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z"/>
                          </svg>
                        </button>
                        <button
                          className={`btn btn-sm ${deleteConfirm === u._id ? 'btn-danger' : 'btn-outline-danger'}`}
                          onClick={() => handleDelete(u._id)}
                          style={{ borderRadius: '0.5rem' }}
                        >
                          {deleteConfirm === u._id ? (
                            'Confirm?'
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredItems.length > pageSize && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ borderRadius: '0.5rem 0 0 0.5rem', color: currentPage === 1 ? '#6c757d' : primaryColor }}
              >
                Prev
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(p => (
              <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPage(p)}
                  style={{
                    backgroundColor: currentPage === p ? primaryColor : 'transparent',
                    borderColor: currentPage === p ? primaryColor : '#dee2e6',
                    color: currentPage === p ? 'white' : primaryColor
                  }}
                >
                  {p}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ borderRadius: '0 0.5rem 0.5rem 0', color: currentPage === totalPages ? '#6c757d' : primaryColor }}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {editing && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content" style={{ borderRadius: '0.5rem' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6' }}>
                <h5 className="modal-title" style={{ color: primaryColor }}>Edit User</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditing(null)}
                ></button>
              </div>
              <div className="modal-body">
                <UserForm
                  user={editing}
                  onSaved={() => {
                    setEditing(null);
                    dispatch(loadUsers());
                  }}
                  onCancel={() => setEditing(null)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
